import React, { useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'sonner';
import { t } from 'i18next';
import { ArrowLeft } from 'lucide-react';
import { RadixButton, RadixCard, RadixInput, RadixSpinner } from '@/components/radix';
import { confirmStorePasswordResetV3Api } from '@/utils/Api/AuthV3';
import { useLoginV3Flow } from './LoginV3FlowProvider';

export default function LoginV3ResetPasswordCard() {
    const { state, goBackInFlow, onResetPasswordSuccess } = useLoginV3Flow();
    const { email, otp } = state.reset;
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [isPending, setIsPending] = useState(false);

    const formik = useFormik({
        initialValues: { password: '', confirm_password: '' },
        validationSchema: Yup.object({
            password: Yup.string().required(t('Common.LoginResetPasswordRequired')),
            confirm_password: Yup.string()
                .oneOf([Yup.ref('password')], t('Common.LoginResetPasswordMismatch'))
                .required(t('Common.LoginResetConfirmRequired')),
        }),
        onSubmit: async (values) => {
            try {
                setIsPending(true);
                await confirmStorePasswordResetV3Api({
                    email,
                    otp,
                    password: values.password,
                    password_confirm: values.confirm_password,
                });
                toast.success(t('Common.LoginResetPasswordUpdated'));
                onResetPasswordSuccess();
            } catch {
                toast.error(t('Common.LoginResetPasswordError'));
            } finally {
                setIsPending(false);
            }
        },
    });

    return (
        <RadixCard className="!p-0 border !border-[#E6E1D9] shadow-xl rounded-md bg-white/95 backdrop-blur-md overflow-hidden">
            <div className="relative bg-white p-6 sm:p-8 pb-4 text-center border-b border-[#F0EDE8]">
                <button
                    type="button"
                    onClick={goBackInFlow}
                    aria-label={t('Common.Back')}
                    className="absolute left-4 top-1/2 -translate-y-1/2 p-2 text-gray-400 hover:text-primary-500 transition-colors bg-transparent border-none outline-none cursor-pointer"
                >
                    <ArrowLeft size={20} />
                </button>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">
                    {t('Common.ResPassConfT')}
                </h1>
                <p className="text-gray-500 text-[11px] sm:text-sm mt-1 font-medium">{t('Common.resConfD1')}</p>
            </div>

            <div className="p-6 sm:p-8">
                <form onSubmit={formik.handleSubmit} className="space-y-4 sm:space-y-6">
                    <RadixInput
                        label={t('Common.NewPassP')}
                        type={showPassword ? 'text' : 'password'}
                        placeholder={t('Common.NewPassP')}
                        value={formik.values.password}
                        onChange={(e) => formik.setFieldValue('password', e.target.value.trim())}
                        error={formik.touched.password ? formik.errors.password : undefined}
                        className="!rounded-xl !bg-white !h-12 sm:!h-14"
                        endComponent={
                            <span className="pointer-events-auto">
                                <button
                                    type="button"
                                    className="pr-3 text-gray-400 hover:text-primary-500 transition-colors bg-transparent border-none outline-none cursor-pointer"
                                    onClick={() => setShowPassword(!showPassword)}
                                >
                                    {showPassword ? <EyeOffIcon /> : <EyeIcon />}
                                </button>
                            </span>
                        }
                    />

                    <RadixInput
                        label={t('Common.ConfPassp')}
                        type={showConfirm ? 'text' : 'password'}
                        placeholder={t('Common.ConfPassp')}
                        value={formik.values.confirm_password}
                        onChange={(e) => formik.setFieldValue('confirm_password', e.target.value.trim())}
                        error={formik.touched.confirm_password ? formik.errors.confirm_password : undefined}
                        className="!rounded-xl !bg-white !h-12 sm:!h-14"
                        endComponent={
                            <span className="pointer-events-auto">
                                <button
                                    type="button"
                                    className="pr-3 text-gray-400 hover:text-primary-500 transition-colors bg-transparent border-none outline-none cursor-pointer"
                                    onClick={() => setShowConfirm(!showConfirm)}
                                >
                                    {showConfirm ? <EyeOffIcon /> : <EyeIcon />}
                                </button>
                            </span>
                        }
                    />

                    <RadixButton
                        type="submit"
                        variant="primary"
                        className="w-full !h-12 sm:!h-14 !rounded-xl !text-base font-bold shadow-lg shadow-primary-500/10 text-white"
                        disabled={isPending}
                    >
                        {isPending ? <RadixSpinner size="sm" /> : t('Common.ResOTpSubBTN')}
                    </RadixButton>
                </form>
            </div>
        </RadixCard>
    );
}

const EyeIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-5 h-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
        <circle cx="12" cy="12" r="3" />
    </svg>
);

const EyeOffIcon = () => (
    <svg
        xmlns="http://www.w3.org/2000/svg"
        className="w-5 h-5"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
    >
        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"></path>
        <line x1="1" y1="1" x2="23" y2="23"></line>
    </svg>
);
